/** @jsxImportSource theme-ui */
import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Flex, Grid, Heading, Label, Spinner, Text } from 'theme-ui';
import { Dialog, DialogBackdrop, useDialogState } from 'reakit/Dialog';
import Button from '@/components/Button';
import { useGetCompanyFeatureFlagsQuery, useSetCompanyFeatureFlagMutation } from '@/store/apiService';
import { toast } from 'react-toastify';
import { CompanyFeatureFlagResponse } from '@/types/types';

interface Props {
    companyId: string;
    companyName?: string;
    onClose: () => void;
}

const AddFeatureFlagModal: React.FC<Props> = ({ companyId, companyName, onClose }) => {
    const dialog = useDialogState({ visible: true, modal: true });
    const { data, isLoading } = useGetCompanyFeatureFlagsQuery({ CompanyId: companyId });
    const [setFlag] = useSetCompanyFeatureFlagMutation();

    const [selected, setSelected] = useState<Map<number, boolean>>(new Map());
    const [initial, setInitial] = useState<Map<number, boolean>>(new Map());
    const [saving, setSaving] = useState(false);

    const flags = data?.Results ?? [];

    useEffect(() => {
        if (!flags.length) return;
        const map = new Map(flags.map((f) => [f.Id!, f.IsEnabled ?? false]));
        setSelected(new Map(map));
        setInitial(new Map(map));
    }, [data]);

    useEffect(() => {
        if (!dialog.visible) onClose();
    }, [dialog.visible]);

    const toggle = (flag: CompanyFeatureFlagResponse) => {
        if (flag.IsPublic) return;
        setSelected((prev) => {
            const next = new Map(prev);
            next.set(flag.Id!, !prev.get(flag.Id!));
            return next;
        });
    };

    const handleSave = async () => {
        const changed = flags.filter(
            (f) => !f.IsPublic && selected.get(f.Id!) !== initial.get(f.Id!),
        );

        if (!changed.length) {
            onClose();
            return;
        }

        setSaving(true);
        const results = await Promise.allSettled(
            changed.map((f) =>
                setFlag({ CompanyId: companyId, Id: f.Id!, IsEnabled: selected.get(f.Id!)! }).unwrap(),
            ),
        );
        setSaving(false);

        const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
        failed.forEach((r) => {
            toast.error(
                r.reason?.data?.ResponseStatus?.Message ?? 'Failed to update feature flag',
                { autoClose: 5000 },
            );
        });

        if (!failed.length) {
            onClose();
        }
    };

    return (
        <DialogBackdrop {...dialog}>
            <Dialog
                sx={{ '&&': { top: 'calc(50% - 200px)', padding: 4, minWidth: '400px', maxWidth: '560px' } }}
                {...dialog}
                aria-label="Add feature flag"
            >
                {(props) => (
                    <Grid {...props} sx={{ gap: 3 }}>
                        <Heading>Feature flags{companyName ? ` for ${companyName}` : ''}</Heading>
                        {isLoading && (
                            <Flex sx={{ justifyContent: 'center', py: 3 }}>
                                <Spinner size={24} />
                            </Flex>
                        )}
                        {!isLoading && flags.length === 0 && (
                            <Text sx={{ color: 'gray.6' }}>No feature flags available.</Text>
                        )}
                        {!isLoading && flags.length > 0 && (
                            <Box sx={{ maxHeight: '320px', overflowY: 'auto' }}>
                                {flags.map((flag) => (
                                    <Flex
                                        key={flag.Id}
                                        sx={{
                                            alignItems: 'center',
                                            py: 2,
                                            opacity: flag.IsPublic ? 0.5 : 1,
                                            borderBottom: '1px solid',
                                            borderColor: 'gray.2',
                                        }}
                                    >
                                        <Box sx={{ flex: 1 }}>
                                            <Text sx={{ fontWeight: 'bold', fontSize: 1 }}>{flag.Name}</Text>
                                            {flag.IsPublic && (
                                                <Text sx={{ fontSize: 0, color: 'gray.5' }}>
                                                    Platform-controlled
                                                </Text>
                                            )}
                                        </Box>
                                        <Label
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                width: 'auto',
                                                cursor: flag.IsPublic ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            <Text sx={{ fontSize: 0, color: 'gray.6' }}>Enabled</Text>
                                            <Checkbox
                                                checked={selected.get(flag.Id!) ?? false}
                                                disabled={!!flag.IsPublic}
                                                onChange={() => toggle(flag)}
                                            />
                                        </Label>
                                    </Flex>
                                ))}
                            </Box>
                        )}
                        <Flex sx={{ gap: 2, justifyContent: 'flex-end' }}>
                            <Button variant="secondary" disabled={saving} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button isLoading={saving} disabled={saving || isLoading} onClick={handleSave}>
                                Save
                            </Button>
                        </Flex>
                    </Grid>
                )}
            </Dialog>
        </DialogBackdrop>
    );
};

export default AddFeatureFlagModal;
